import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlined";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import WarningIcon from "@mui/icons-material/Warning";
import WatchLaterIcon from "@mui/icons-material/WatchLater";
import { Box, Typography } from "@mui/material";
import { memo } from "react";
import ColorfulRole from "../../components/UIToolkit/ColorfulRole/ColorfulRole";
import { STATUS_BADGE } from "../../theme/tokens";
import { AgentStaffAction, CallStatus } from "../../utils/models";
import { ShiftFinalStatusKind, type TranslateFn } from "./shiftPickupReportStatus";

/**
 * Title-cased role-group / job pills for a raw interaction row, mirroring the grouped view's
 * role-group cell. `roleGroups` is already resolved (job name when job-management is on, else the
 * shift's role groups) by the page so this stays presentational.
 */
export const RawRoleGroupCell = memo(({ roleGroups }: { roleGroups: string[] }) => {
	if (!roleGroups.length) return <>—</>;
	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
			{roleGroups.map((roleGroup) => (
				<ColorfulRole key={roleGroup} roleGroup={roleGroup.charAt(0).toUpperCase() + roleGroup.slice(1)} />
			))}
		</Box>
	);
});
RawRoleGroupCell.displayName = "RawRoleGroupCell";

// Status icon keyed off the language-independent status kind (never the localized label).
const getStatusIcon = (kind: ShiftFinalStatusKind) => {
	switch (kind) {
		case ShiftFinalStatusKind.DoneAccomplished:
			return <CheckCircleOutlineIcon sx={{ fontSize: "16px", color: "#4CAF50" }} />;
		case ShiftFinalStatusKind.DoneNotAccomplished:
			return <CancelIcon sx={{ fontSize: "16px", color: "#E71C00" }} />;
		case ShiftFinalStatusKind.DonePartially:
			return <WarningIcon sx={{ fontSize: "16px", color: STATUS_BADGE.ORANGE }} />;
		case ShiftFinalStatusKind.Paused:
			return <PauseCircleIcon sx={{ fontSize: "16px", color: "primary.dark" }} />;
		case ShiftFinalStatusKind.Pending:
			return <AccessTimeIcon sx={{ fontSize: "16px", color: "#999" }} />;
		case ShiftFinalStatusKind.NotInitiated:
			return <WatchLaterIcon sx={{ fontSize: "16px", color: "#999" }} />;
		default:
			return null;
	}
};

/** Grouped-view status cell: the localized status label preceded by its kind-driven icon. */
export const GroupedStatusCell = memo(({ status, statusKind }: { status: string; statusKind: ShiftFinalStatusKind }) => (
	<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
		{getStatusIcon(statusKind)}
		<Typography variant="body2" sx={{ color: "primary.dark" }}>
			{status}
		</Typography>
	</Box>
));
GroupedStatusCell.displayName = "GroupedStatusCell";

/** Twilio call statuses that mean the nurse picked up. */
const ANSWERED_CALL_STATUSES = new Set<CallStatus>([CallStatus.Completed, CallStatus.InProgress]);
/** Twilio call statuses that mean the nurse did not pick up (terminal failures). */
const NOT_ANSWERED_CALL_STATUSES = new Set<CallStatus>([CallStatus.NoAnswer, CallStatus.Busy, CallStatus.Failed, CallStatus.Canceled]);

// Message lifecycle string is the nurse-action status (NOT a Twilio delivery status — see flag #1
// in the plan). The "canceled" string has no member in the AgentStaffActionStatus enum, so it's
// matched as a raw string here. Values are translation keys, resolved via `t` at render.
const MESSAGE_LIFECYCLE_LABEL_KEYS: Record<string, string> = {
	completed: "shift_pickup_report.interaction_status.sent",
	pending: "shift_pickup_report.interaction_status.pending",
	canceled: "shift_pickup_report.interaction_status.canceled",
};

/**
 * Interaction-status cell label, localized via `t`.
 * - Calls: Twilio `response_status` (falling back to the raw `interaction_status` string) →
 *   "Answered" / "Not answered" for terminal states, or the raw status for transient states
 *   (ringing/queued/…).
 * - Messages: nurse-action lifecycle → "Sent" / "Pending" / "Canceled" (raw status otherwise).
 */
export const getInteractionStatusLabel = (
	t: TranslateFn,
	interactionType: AgentStaffAction,
	responseStatus: CallStatus | null,
	interactionStatus: string,
): string => {
	if (interactionType === AgentStaffAction.Call) {
		const callStatus = responseStatus ?? (interactionStatus as CallStatus);
		if (ANSWERED_CALL_STATUSES.has(callStatus)) return t("shift_pickup_report.interaction_status.answered");
		if (NOT_ANSWERED_CALL_STATUSES.has(callStatus)) return t("shift_pickup_report.interaction_status.not_answered");
		// Transient states (queued / ringing): surface the raw status verbatim.
		return interactionStatus;
	}

	const labelKey = MESSAGE_LIFECYCLE_LABEL_KEYS[interactionStatus];
	return labelKey ? t(labelKey) : interactionStatus;
};
